import { useRef, useState } from 'react'
import { get, upload } from '../../../../../utils/Api'
import { useBooking } from './ctx'
import { LIMITS, PRESETS, PURPOSES, STATE_NAMES } from './constants'
import { Chips, Combo, Field, Ic, Note, OptionCards, SelectField } from './fields'
import type { ComboOption } from './fields'
import { blankParcel, formKind, isIntl, naira, num, volKg } from './rules'
import type { Customer } from './types'

export const toCustomer = (u: any): Customer => ({
  ...u,
  label: `${u.firstname} ${u.lastname}`,
  value: u._id,
  firstname: u.firstname,
  lastname: u.lastname,
  phonenumber: u.phonenumber,
  email: u.email,
  balance: parseFloat(Number(u.balance || 0).toFixed(2)),
  creditLimit: Number(u.creditLimit || 0),
  debt: parseFloat(Number(u.debt || 0).toFixed(2)),
  turnoff_payment: !!u.turnoff_payment,
})

function CustomerPicker() {
  const { customer, pickCustomer, err } = useBooking()
  const [q, setQ] = useState('')
  const [opts, setOpts] = useState<ComboOption<Customer>[]>([])
  const [loading, setLoading] = useState(false)
  const timer = useRef<any>(null)
  const seq = useRef(0)

  const search = (text: string) => {
    setQ(text)
    clearTimeout(timer.current)
    if (text.trim().length < 2) {
      setOpts([])
      return
    }
    timer.current = setTimeout(() => {
      const mine = ++seq.current
      setLoading(true)
      get(`admin/booking-users?search=${encodeURIComponent(text.trim())}`)
        .then((result: any) => {
          if (mine !== seq.current) return
          setOpts(
            result.status === 200
              ? result.data.slice(0, 8).map((u: any) => ({
                  key: u._id,
                  title: `${u.firstname} ${u.lastname}`,
                  sub: [u.email, u.phonenumber].filter(Boolean).join(' · '),
                  value: toCustomer(u),
                }))
              : [],
          )
        })
        .catch(() => setOpts([]))
        .finally(() => mine === seq.current && setLoading(false))
    }, 300)
  }

  if (customer) {
    const c = customer
    return (
      <div className='bk-pcard'>
        <span className='av' aria-hidden='true'>
          {(c.firstname?.[0] || '') + (c.lastname?.[0] || '')}
        </span>
        <div className='pc-b'>
          <b>
            {c.firstname} {c.lastname}
          </b>
          <p>{[c.email, c.phonenumber].filter(Boolean).join(' · ')}</p>
          <span className='bk-badge blue'>
            <Ic name='wallet' /> Wallet {naira(c.balance)}
          </span>
          {c.turnoff_payment && (
            <span className='bk-badge'>
              <Ic name='badge-check' /> Credit {naira(Math.max(0, c.creditLimit - c.debt))} left
            </span>
          )}
          {c.debt > 0 && (
            <span className='bk-badge warn'>
              <Ic name='circle-alert' /> Owes {naira(c.debt)}
            </span>
          )}
        </div>
        <button type='button' className='bk-btn quiet sm' id='cust-change' onClick={() => pickCustomer(null)}>
          Change<span className='bk-sr'> customer</span>
        </button>
      </div>
    )
  }
  return (
    <Combo
      id='cust'
      label='Customer'
      hint='Search by name, email or phone number'
      value={q}
      onInput={search}
      options={opts}
      loading={loading}
      placeholder='e.g. Chioma or 0803…'
      emptyText={q.trim().length >= 2 ? 'No customer matches that. Check the spelling or search by phone.' : ''}
      error={err('cust')}
      onPick={(o) => {
        setQ('')
        setOpts([])
        pickCustomer(o.value)
      }}
    />
  )
}

function PhotoField() {
  const { f, set, notify } = useBooking()
  const [busy, setBusy] = useState(false)
  const onFile = (file?: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) return notify('error', 'Choose an image file (JPG or PNG).')
    if (file.size > LIMITS.photoBytes) return notify('error', 'The photo must be 3 MB or smaller.')
    const fd = new FormData()
    fd.append('images', file)
    setBusy(true)
    upload(fd)
      .then((result: any) => {
        if (result.status === 201) set((d) => void (d.photo = result.image))
        else notify('error', result.message || 'Failed to upload the photo')
      })
      .catch(() => notify('error', 'Failed to upload the photo'))
      .finally(() => setBusy(false))
  }
  return (
    <div className='bk-field'>
      <span className='bk-lbl'>
        Photo of the parcel <span className='bk-optional'>(optional)</span>
      </span>
      <input
        type='file'
        accept='image/*'
        id='photo-in'
        className='bk-sr bk-file'
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      {busy ? (
        <div className='bk-drop' role='status'>
          <span className='oi'>
            <Ic name='loader-circle' className='spin' />
          </span>
          <span>
            <b>Uploading…</b>
          </span>
        </div>
      ) : f.photo ? (
        <div className='bk-preview'>
          <img src={f.photo} alt='Parcel photo' />
          <div className='pv-b'>
            <b>Photo added</b>
          </div>
          <label htmlFor='photo-in' className='bk-btn quiet sm'>
            Replace
          </label>
          <button type='button' className='bk-btn danger-quiet sm' onClick={() => set((d) => void (d.photo = ''))}>
            Remove
          </button>
        </div>
      ) : (
        <label htmlFor='photo-in' className='bk-drop'>
          <span className='oi'>
            <Ic name='image-plus' />
          </span>
          <span>
            <b>Add a photo</b>
            <span>Helps settle damage claims. JPG or PNG, up to 3 MB.</span>
          </span>
        </label>
      )}
    </div>
  )
}

export default function RouteStep() {
  const { f, set, err, clearErr, countries, draft } = useBooking()
  const k = formKind(f)
  const countryOpts = countries.map((c) => ({ value: c.isoCode, label: c.name }))
  const stateOpts = STATE_NAMES.map((s) => ({ value: s, label: s }))

  const setRoute = (key: 'from' | 'to', v: string) =>
    set((d) => {
      d[key] = v
      const nk = formKind(d)
      // GetPriceUseCase rejects domestic documents
      if (nk === 'domestic' && d.type === 'document') d.type = 'package'
      // Addresses belong to the old country; start them again
      const who = key === 'from' ? 'sender' : 'receiver'
      d[who].addr = { l1: '', l2: '', city: '', state: '', pc: '' }
      d[who].addrMode = 'search'
    })

  const kindNote = {
    domestic: ['truck', 'Domestic delivery within Nigeria.'],
    export: [
      'plane-takeoff',
      f.type === 'package'
        ? 'Export from Nigeria. You’ll list each item for customs in a later step.'
        : 'Export from Nigeria. Documents don’t need a customs item list.',
    ],
    import: [
      'plane-landing',
      `Import into Nigeria. DHL collects from the sender abroad.${f.type === 'package' ? ' You’ll list each item for customs.' : ''}`,
    ],
  } as const

  return (
    <>
      {draft && (
        <Note tone='warn' icon='scale'>
          <p>
            <b>Weigh and measure the parcel first.</b> The customer entered{' '}
            {draft.kg.map((x) => `${x} kg`).join(', ')}. Correct the size and weight below if the scale says
            otherwise — the price updates as you go.
          </p>
        </Note>
      )}

      <fieldset className='bk-grp'>
        <legend>Booking for</legend>
        <CustomerPicker />
      </fieldset>

      <fieldset className='bk-grp'>
        <legend>Where is it going?</legend>
        <div className='bk-route'>
          <SelectField
            id='route-from'
            label='From'
            value={f.from}
            options={countryOpts}
            placeholder='Select country'
            error={err('route-from')}
            onChange={(v) => {
              setRoute('from', v)
              clearErr('route-from', 'route-to')
            }}
          />
          <button
            type='button'
            className='bk-swap'
            aria-label='Swap from and to countries'
            onClick={() =>
              set((d) => {
                ;[d.from, d.to] = [d.to, d.from]
                ;[d.fromState, d.toState] = [d.toState, d.fromState]
                ;[d.sender.addr, d.receiver.addr] = [d.receiver.addr, d.sender.addr]
                ;[d.sender.addrMode, d.receiver.addrMode] = [d.receiver.addrMode, d.sender.addrMode]
              })
            }
          >
            <Ic name='arrow-left-right' />
          </button>
          <SelectField
            id='route-to'
            label='To'
            value={f.to}
            options={countryOpts}
            placeholder='Select country'
            error={err('route-to')}
            onChange={(v) => {
              setRoute('to', v)
              clearErr('route-to')
            }}
          />
        </div>
        {k && k !== 'invalid' && (
          <Note tone='info' icon={kindNote[k][0]}>
            <p>{kindNote[k][1]}</p>
          </Note>
        )}
        {k === 'domestic' && (
          <div className='bk-row c2 mt'>
            <SelectField
              id='route-fstate'
              label='From state'
              value={f.fromState}
              options={stateOpts}
              placeholder='Select state'
              error={err('route-fstate')}
              onChange={(v) => {
                set((d) => {
                  d.fromState = v
                  d.sender.addr.state = v
                  d.sender.addr.city = ''
                })
                clearErr('route-fstate')
              }}
            />
            <SelectField
              id='route-tstate'
              label='To state'
              value={f.toState}
              options={stateOpts}
              placeholder='Select state'
              error={err('route-tstate')}
              onChange={(v) => {
                set((d) => {
                  d.toState = v
                  d.receiver.addr.state = v
                  d.receiver.addr.city = ''
                })
                clearErr('route-tstate')
              }}
            />
          </div>
        )}
      </fieldset>

      <OptionCards
        name='type'
        legend='What are they sending?'
        value={f.type}
        onChange={(v) => set((d) => void (d.type = v as 'package' | 'document'))}
        options={[
          {
            value: 'package',
            title: 'Package',
            sub: 'Goods, gifts, samples — anything that isn’t only paperwork',
            icon: 'package',
          },
          k === 'domestic'
            ? {
                value: 'document',
                title: 'Documents',
                sub: 'Not available within Nigeria — send papers as a Package.',
                icon: 'file-text',
                disabled: true,
              }
            : {
                value: 'document',
                title: 'Documents',
                sub: 'Papers and certificates only, up to 2 kg.',
                icon: 'file-text',
              },
        ]}
      />

      <fieldset className='bk-grp'>
        <legend>Size and weight</legend>
        <p className='bk-intro'>Pick a box size or enter your own. We charge the higher of actual and volumetric weight.</p>
        {f.parcels.map((p, i) => {
          const lbl = f.parcels.length > 1 ? `Parcel ${i + 1}` : 'The parcel'
          const v = volKg(p)
          const higher = v > num(p.w)
          return (
            <div className='bk-parcel' role='group' aria-labelledby={`p${i}-h`} key={i}>
              <div className='bk-parcel-h'>
                <h3 id={`p${i}-h`}>{lbl}</h3>
                {f.parcels.length > 1 && (
                  <button
                    type='button'
                    className='bk-btn danger-quiet sm'
                    onClick={() => set((d) => void d.parcels.splice(i, 1))}
                  >
                    <Ic name='trash-2' /> Remove<span className='bk-sr'> {lbl}</span>
                  </button>
                )}
              </div>
              <div className='bk-chips presets' role='radiogroup' aria-label={`${lbl} size`}>
                {PRESETS.map((pr) => (
                  <label className='bk-chip' key={pr.key}>
                    <input
                      type='radio'
                      name={`preset-${i}`}
                      checked={p.preset === pr.key}
                      onChange={() => {
                        set((d) => {
                          Object.assign(d.parcels[i], { preset: pr.key, l: String(pr.l), wd: String(pr.wd), h: String(pr.h) })
                        })
                        clearErr(`p${i}-l`, `p${i}-wd`, `p${i}-h`)
                      }}
                    />
                    {pr.name}
                    <small>
                      {pr.l}×{pr.wd}×{pr.h}
                    </small>
                  </label>
                ))}
                <label className='bk-chip'>
                  <input
                    type='radio'
                    name={`preset-${i}`}
                    checked={p.preset === 'custom'}
                    onChange={() => set((d) => void (d.parcels[i].preset = 'custom'))}
                  />
                  Custom<small>Own sizes</small>
                </label>
              </div>
              <div className='bk-row'>
                <Field
                  id={`p${i}-w`}
                  label={draft ? 'Weight on the scale' : 'Weight'}
                  hint={draft && draft.kg[i] != null ? `Customer entered ${draft.kg[i]} kg` : undefined}
                  value={p.w}
                  type='number'
                  inputMode='decimal'
                  min='0.5'
                  step='0.1'
                  suf='kg'
                  error={err(`p${i}-w`)}
                  onChange={(val) => {
                    set((d) => void (d.parcels[i].w = val))
                    clearErr(`p${i}-w`)
                  }}
                />
              </div>
              <div className='bk-row dims'>
                {(
                  [
                    ['l', 'Length'],
                    ['wd', 'Width'],
                    ['h', 'Height'],
                  ] as const
                ).map(([key, name]) => (
                  <Field
                    key={key}
                    id={`p${i}-${key}`}
                    label={`${name} (cm)`}
                    value={p[key]}
                    type='number'
                    inputMode='decimal'
                    min='1'
                    error={err(`p${i}-${key}`)}
                    onChange={(val) => {
                      set((d) => {
                        d.parcels[i][key] = val
                        d.parcels[i].preset = 'custom'
                      })
                      clearErr(`p${i}-${key}`)
                    }}
                  />
                ))}
              </div>
              <p className='bk-vol' aria-live='polite'>
                <Ic name={!v ? 'info' : higher ? 'circle-alert' : 'check'} />
                {!v ? (
                  <span>Enter all three sides to see the volumetric weight.</span>
                ) : (
                  <span>
                    Volumetric weight <b>{v.toFixed(1)} kg</b>
                    {num(p.w)
                      ? higher
                        ? ' — higher than the actual weight, so this is charged.'
                        : ' — lower than the actual weight.'
                      : '.'}
                  </span>
                )}
              </p>
            </div>
          )
        })}
        <button type='button' className='bk-btn secondary sm' onClick={() => set((d) => void d.parcels.push(blankParcel()))}>
          <Ic name='plus' /> Add another parcel
        </button>
      </fieldset>

      <fieldset className='bk-grp'>
        <legend>What’s inside?</legend>
        <Field
          id='contents'
          label='Short description'
          hint={`${f.type === 'document' ? 'e.g. “University transcripts”.' : 'e.g. “Shea butter and black soap”.'} Printed on the waybill — up to ${LIMITS.contents} characters.`}
          value={f.contents}
          maxLength={LIMITS.contents}
          error={err('contents')}
          onChange={(v) => {
            set((d) => void (d.contents = v))
            clearErr('contents')
          }}
        />
        {f.type === 'package' && (
          <Field
            id='value'
            label='What is it worth?'
            hint='The total value of everything in the parcel, in naira. Used for shipment protection and the price.'
            value={f.value}
            type='number'
            inputMode='decimal'
            min='1'
            pre='₦'
            error={err('value')}
            onChange={(v) => {
              set((d) => void (d.value = v))
              clearErr('value', 'meter-v')
            }}
          />
        )}
        {isIntl(f) && (
          <Chips
            name='purpose'
            label='Why is it being sent?'
            value={f.purpose}
            options={PURPOSES}
            error={err('purpose')}
            onChange={(v) => {
              set((d) => void (d.purpose = v))
              clearErr('purpose')
            }}
          />
        )}
        <PhotoField />
      </fieldset>
    </>
  )
}
